import React, { useMemo } from "react";
import {
  faCalendarAlt,
  faEye,
  faEyeSlash,
  faMoneyBillWave,
  faPen,
  faToggleOff,
  faToggleOn,
  faTrash,
  faUserPlus,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";

import { Badge } from "react-bootstrap";
import CustomCard from "../../../../commons/cards/Card";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

function formatBRL(v) {
  const n = Number(v || 0);
  try {
    return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  } catch {
    return `R$ ${n.toFixed(2)}`;
  }
}

function frequencyLabel(v) {
  switch (v) {
    case "MONTHLY":
      return "Mensal";
    case "QUARTERLY":
      return "Trimestral";
    case "YEARLY":
      return "Anual";
    default:
      return v || "—";
  }
}

function billingRuleLabel(v, fixedDay) {
  if (v === "FIXED_DAY_OF_MONTH") return `Dia fixo (${fixedDay || "—"})`;
  if (v === "ON_SUBSCRIPTION_DAY") return "Dia da adesão";
  return v || "—";
}

function statusVariant(active) {
  return active ? "success" : "secondary";
}

function visibilityVariant(visible) {
  return visible ? "primary" : "light";
}

function visibilityTextColor(visible) {
  return visible ? undefined : "dark";
}

function ActionPillButton({ color, borderColor, icon, label, onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        borderRadius: 10,
        border: "1px solid",
        borderColor: borderColor || "#e8e8f0",
        background: "#fff",
        color: color || "#111827",
        padding: "6px 10px",
        fontSize: 11,
        fontWeight: 900,
        cursor: "pointer",
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
      }}
    >
      <FontAwesomeIcon icon={icon} />
      {label}
    </button>
  );
}

export default function ClubPlanCard({ plan, onEdit, onToggleActive, onDelete, onAddCustomers, onViewCustomers }) {
  const active = !!plan?.active;
  const visible = !!plan?.visibleOnSite;

  const priceLabel = useMemo(() => formatBRL(plan?.amount), [plan?.amount]);

  const subtitle = useMemo(() => {
    const freq = frequencyLabel(plan?.frequency);
    const rule = billingRuleLabel(plan?.billingRule, plan?.fixedDayOfMonth);
    return `${priceLabel} • ${freq} • ${rule}`;
  }, [plan?.frequency, plan?.billingRule, plan?.fixedDayOfMonth, priceLabel]);

  return (
    <div style={{ width: "100%" }}>
      <CustomCard>
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between gap-2">
          <div style={{ minWidth: 0 }}>
            <div className="fw-semibold" style={{ fontSize: 16, lineHeight: "20px" }}>
              {plan?.name || "Clube"}
            </div>

            <div className="text-muted" style={{ fontSize: 12 }}>
              {plan?.shortDescription || subtitle}
            </div>
          </div>

          <div className="d-flex flex-column align-items-end gap-2">
            <Badge bg={statusVariant(active)} pill>
              {active ? "Ativo" : "Inativo"}
            </Badge>

            <ActionPillButton
              icon={active ? faToggleOff : faToggleOn}
              label={active ? "Desativar" : "Ativar"}
              onClick={onToggleActive}
              color={active ? "#b42318" : "#198754"}
              borderColor={active ? "#ffd6d6" : "#c9f2d8"}
              title={active ? "Desativar clube" : "Ativar clube"}
            />
          </div>
        </div>

        <hr className="my-3" />

        {/* Badges complementares */}
        <div className="d-flex flex-wrap gap-2 mb-3">
          <Badge bg={visibilityVariant(visible)} text={visibilityTextColor(visible)} pill style={{ fontWeight: 900 }}>
            <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} className="me-1" />
            {visible ? "Visível no site" : "Oculto no site"}
          </Badge>

          <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
            <FontAwesomeIcon icon={faMoneyBillWave} className="me-1" />
            {priceLabel}
          </Badge>

          <Badge bg="light" text="dark" pill style={{ fontWeight: 900 }}>
            <FontAwesomeIcon icon={faCalendarAlt} className="me-1" />
            {frequencyLabel(plan?.frequency)}
          </Badge>
        </div>

        {/* Infos principais */}
        <div className="d-flex flex-column gap-2">
          <div className="d-flex align-items-center justify-content-between">
            <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
              <span style={{ fontWeight: 900 }}>#</span>
              <span>Limite de assinantes</span>
            </div>
            <div className="fw-semibold" style={{ fontSize: 13, textAlign: "right" }}>
              {plan?.limitSubscribers != null ? plan.limitSubscribers : "—"}
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
              <span style={{ fontWeight: 900 }}>↕</span>
              <span>Ordem de exibição</span>
            </div>
            <div className="fw-semibold" style={{ fontSize: 13 }}>
              {plan?.displayOrder != null ? plan.displayOrder : "—"}
            </div>
          </div>

          <div className="d-flex align-items-center justify-content-between">
            <div className="text-muted d-flex align-items-center gap-2" style={{ fontSize: 13 }}>
              <span style={{ fontWeight: 900 }}>∞</span>
              <span>Múltiplas assinaturas</span>
            </div>
            <div className="fw-semibold" style={{ fontSize: 13 }}>
              {plan?.allowMultipleSubscriptionsPerCustomer ? "Sim" : "Não"}
            </div>
          </div>
        </div>

        {/* Ações */}
        <div className="d-flex gap-2 flex-wrap mt-3">
          <ActionPillButton
            icon={faUserPlus}
            label="Adicionar Cliente"
            onClick={onAddCustomers}
            color="#059669"
            borderColor="#d1fae5"
            title="Adicionar clientes ao clube"
          />

          <ActionPillButton
            icon={faUsers}
            label="Ver Clientes"
            onClick={onViewCustomers}
            color="#0891b2"
            borderColor="#cffafe"
            title="Ver clientes do clube"
          />

          <ActionPillButton
            icon={faPen}
            label="Editar"
            onClick={onEdit}
            color="#2f2a7a"
            borderColor="#dfe3ff"
            title="Editar clube"
          />

          <ActionPillButton
            icon={faTrash}
            label="Excluir"
            onClick={onDelete}
            color="#b42318"
            borderColor="#ffd6d6"
            title="Excluir clube"
          />
        </div>

        <div className="text-muted mt-3" style={{ fontSize: 11 }}>
          Dica: edite para ajustar benefícios e regras.
        </div>
      </CustomCard>
    </div>
  );
}
